import { useState, useEffect, useRef, useCallback } from 'react';

export interface LogStreamEvent {
  type: 'log' | 'execution_complete' | 'ping';
  execution_id: string;
  timestamp?: string;
  action?: string;
  resource_id?: string;
  status?: string;
  model?: string;
  input_tokens?: number;
  output_tokens?: number;
  ai_summary?: string;
  duration?: number;
}

interface UseExecutionLogStreamResult {
  events: LogStreamEvent[];
  isConnected: boolean;
  isComplete: boolean;
}

export function useExecutionLogStream(
  executionId: string,
  enabled: boolean
): UseExecutionLogStreamResult {
  const [events, setEvents] = useState<LogStreamEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptedRef = useRef(false);
  // Use refs for values accessed in WebSocket callbacks to avoid stale closures
  const isCompleteRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isCompleteRef.current = isComplete;
  }, [isComplete]);

  const connect = useCallback(() => {
    if (!executionId || !enabled) return;
    // Check ref (not state) to avoid stale closure
    if (isCompleteRef.current) return;

    const baseUrl = process.env.REACT_APP_AUTOGEN_BASE_URL || 'https://genie-autogen.azurewebsites.net';

    // Convert HTTP(S) to WebSocket protocol
    let wsBaseUrl;
    if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      wsBaseUrl = baseUrl.replace(/^https?:\/\//, 'ws://');
    } else {
      if (baseUrl.startsWith('https://')) {
        wsBaseUrl = baseUrl.replace(/^https:\/\//, 'wss://');
      } else {
        wsBaseUrl = baseUrl.replace(/^http:\/\//, 'ws://');
      }
    }

    const params = new URLSearchParams();
    const authToken = localStorage.getItem('accessToken');
    if (authToken) {
      params.append('token', authToken);
    }

    const wsUrl = `${wsBaseUrl}/ws/logs/${executionId}?${params.toString()}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        console.log(`[LogStream] Connected for execution ${executionId}`);
      };

      ws.onmessage = (event) => {
        try {
          const data: LogStreamEvent = JSON.parse(event.data);

          if (data.type === 'ping') return;

          if (data.type === 'execution_complete') {
            isCompleteRef.current = true;
            setIsComplete(true);
            setEvents(prev => [...prev, data]);
            ws.close();
            return;
          }

          setEvents(prev => [...prev, data]);
        } catch (e) {
          console.warn('[LogStream] Failed to parse message:', e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);

        // Auto-reconnect once on unexpected disconnect if not complete
        // Use ref to avoid stale closure
        if (!isCompleteRef.current && !reconnectAttemptedRef.current) {
          reconnectAttemptedRef.current = true;
          setTimeout(() => {
            if (!isCompleteRef.current) {
              connect();
            }
          }, 2000);
        }
      };

      ws.onerror = (error) => {
        console.error('[LogStream] WebSocket error:', error);
      };
    } catch (e) {
      console.error('[LogStream] Failed to create WebSocket:', e);
    }
    // Only depend on executionId and enabled — not isComplete (use ref instead)
  }, [executionId, enabled]);

  useEffect(() => {
    if (enabled && executionId) {
      // Reset state on new connection
      setEvents([]);
      setIsComplete(false);
      isCompleteRef.current = false;
      setIsConnected(false);
      reconnectAttemptedRef.current = false;

      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [executionId, enabled, connect]);

  return { events, isConnected, isComplete };
}
